import cn from 'classnames';
import type { NextPage } from 'next';
import { useMemo } from 'react';

import { Layout, Logo } from '@sravni/react-design-system';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getSpecialPageListData } from '@src/config/getSpecialPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import ProductList from '@src/containers/list/ProductList';
import { useListPage } from '@src/hooks/useListPage';
import { useSelectUser } from '@src/reducers/user';
import { Layout as AppLayout } from 'app/Layout';
import { specialPageDisclaimers } from 'shared/config/specialPageDisclaimers';
import { TextList } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { isShowCalculatorByQuery } from 'widgets/FullDealEntryPoint/lib/isShowCalculatorByQuery';

import { getActions, useSpecialPageBanners } from '../lib';

import { CHECKER_QUERY, HEADER_QUERY, LOGO_AND_TEXT_QUERY, NOT_DISPLAY_VALUE } from './SpecialPage.config';
import styles from './SpecialPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.SPEC_OFFERS,
  source: 'search_special',
  sortPanelVisible: false,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
};

export const SpecialPage: NextPage = () => {
  const listKey = ListKeys.SPEC_OFFERS;
  const { filter, pathname, query } = useListPage(listKey);
  const { withCRMBanner, withMobileBanner, isCRMSpecial } = useSpecialPageBanners();
  const user = useSelectUser();

  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: true, listKey }), [listKey]);

  const isShowCalculator = isShowCalculatorByQuery(query);

  const showLogoAndText = query?.[LOGO_AND_TEXT_QUERY] !== NOT_DISPLAY_VALUE;
  const showChecker = query?.[CHECKER_QUERY] !== NOT_DISPLAY_VALUE;
  const showHeaders = query?.[HEADER_QUERY] !== NOT_DISPLAY_VALUE;

  return (
    <AppLayout
      footer={null}
      header={
        showLogoAndText && (
          <Layout>
            <Logo className="h-mt-20" />
          </Layout>
        )
      }
    >
      <PageContext.Provider value={config}>
        <BrandingHeaders
          subHeadingHidden
          showHeaders={showHeaders}
          showSponsorLink={showHeaders}
          containerClassName={cn(styles.header, {
            [styles.headerWithCrmBanner]: withCRMBanner,
            [styles.headerWithMobileBanner]: withMobileBanner,
            [styles.withoutChecker]: !showChecker,
          })}
          afterHeaders={getActions({
            isCRMSpecial,
            showChecker,
            isShowCalculator,
            showHeaders,
            user,
          })}
        />

        <ProductList filter={filter} pathname={pathname} />
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE]} />
      </PageContext.Provider>

      {showLogoAndText && (
        <Layout>
          <TextList className={styles.disclaimer} texts={specialPageDisclaimers} />
        </Layout>
      )}
    </AppLayout>
  );
};

SpecialPage.getInitialProps = async (ctx: Application.ReduxNextPageContext) => {
  await getSpecialPageListData(ctx, {
    key: ListKeys.SPEC_OFFERS,
    defaultFiltersMode: MODES.SPEC_OFFERS,
  });
};
